import { Link, createFileRoute } from "@tanstack/react-router";
import { TriangleAlertIcon, XIcon } from "lucide-react";

import { DebouncedInput } from "~/components/debounced-input";
import { Field } from "~/components/filter-controls";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { StatusBadge } from "~/components/status-badge";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { EM_DASH, formatDate, formatIDR, formatIDROptional } from "~/lib/format";
import { useSettlements } from "~/lib/queries";
import {
  hasActiveFilters,
  toFilters,
  validateSettlementSearch,
  type SettlementSearch,
} from "./-search";

export const Route = createFileRoute("/_authed/settlements/")({
  validateSearch: validateSettlementSearch,
  component: SettlementsPage,
});

const COLUMNS = 6;

/**
 * Settlement batches: DurianPay paying Kugie.
 *
 * Platform-global, and that is the point — `settlements` has no storefront_id,
 * because the per-merchant split does not exist at this layer. There is
 * deliberately no per-merchant column, total or grouping anywhere on this screen
 * or its detail page; that report belongs to the ledger project, and a second
 * version of it here would be a second number that disagrees.
 */
function SettlementsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters = toFilters(search);
  const { data, error, isPending, isFetching, refetch } = useSettlements(filters);

  function patch(next: Partial<SettlementSearch>) {
    void navigate({
      search: (previous) => ({
        ...previous,
        ...next,
        offset: "offset" in next ? next.offset : 0,
      }),
      replace: true,
    });
  }

  function clearFilters() {
    void navigate({ search: (previous) => ({ limit: previous.limit }), replace: true });
  }

  const rows = data?.items ?? [];
  const filtered = hasActiveFilters(search);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Settlements"
        description="What DurianPay has settled into Kugie's account, and the orders inside each batch."
      />

      <FilterBar
        search={search}
        onChange={patch}
        onClear={clearFilters}
        hasFilters={filtered}
      />

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Settled</TableHead>
              <TableHead>PSP settlement ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Settled amount</TableHead>
              <TableHead className="text-right">Fee</TableHead>
              <TableHead className="text-right">Orders</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError
                columns={COLUMNS}
                message={errorMessage(error)}
                onRetry={() => void refetch()}
              />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={
                  filtered
                    ? "No settlements match these filters."
                    : "No settlements have been recorded yet."
                }
                onClear={filtered ? clearFilters : undefined}
              />
            ) : (
              rows.map((row) => (
                <TableRow key={row.settlement_id}>
                  <TableCell>
                    <Link
                      to="/settlements/$settlementId"
                      params={{ settlementId: row.settlement_id }}
                      className="text-primary hover:underline"
                    >
                      {/* Nullable. A settlement with no recorded date is still a
                          real row and still has to be openable. */}
                      {row.settlement_date ? formatDate(row.settlement_date) : "no date"}
                    </Link>
                  </TableCell>

                  <TableCell className="max-w-[16rem] truncate font-mono text-[0.8125rem]">
                    <span className="flex items-center gap-1.5">
                      {row.psp_settlement_id ?? (
                        <span className="font-sans text-muted-foreground">{EM_DASH}</span>
                      )}
                      {/* The model declares this unique and the table does not
                          enforce it, so duplicates are insertable and may exist.
                          Flagged rather than assumed away. */}
                      {row.duplicate_psp_id ? (
                        <TriangleAlertIcon
                          className="size-3.5 shrink-0 text-warning"
                          aria-label="Duplicate PSP settlement ID"
                        />
                      ) : null}
                    </span>
                  </TableCell>

                  <TableCell>
                    {row.status ? (
                      <StatusBadge status={row.status} />
                    ) : (
                      <span className="text-muted-foreground">{EM_DASH}</span>
                    )}
                  </TableCell>

                  {/* The one settlement money column that is genuinely NOT
                      NULL, so it is never an em dash. */}
                  <TableCell className="text-right tabular-nums">
                    {formatIDR(row.settlement_amount)}
                  </TableCell>

                  {/* An absent fee is an em dash, never Rp 0. The column is
                      nullable and "unknown" is not "nothing was charged". */}
                  <TableCell className="text-right tabular-nums">
                    {formatIDROptional(row.settlement_fee)}
                  </TableCell>

                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {row.transaction_count}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar
            pagination={data.pagination}
            onChange={(next) => patch(next)}
            disabled={isFetching}
          />
        ) : null}
      </div>
    </div>
  );
}

function FilterBar({
  search,
  onChange,
  onClear,
  hasFilters,
}: {
  search: SettlementSearch;
  onChange: (patch: Partial<SettlementSearch>) => void;
  onClear: () => void;
  hasFilters: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
      <Field label="PSP settlement ID">
        <DebouncedInput
          value={search.psp_settlement_id ?? ""}
          onCommit={(value) => onChange({ psp_settlement_id: value || undefined })}
          placeholder="Starts with…"
          className="w-56"
        />
      </Field>

      {/* Free text, not a dropdown. settlements.status is an unconstrained
          varchar with no enum behind it — the values in production are whatever
          the settlement ingest has written over the years, and a dropdown built
          from a guess would make real rows unfindable. */}
      <Field label="Status">
        <DebouncedInput
          value={search.status ?? ""}
          onCommit={(value) => onChange({ status: value || undefined })}
          placeholder="Exact match"
          className="w-40"
        />
      </Field>

      {/* Filtered on settlement_date, not created_at: the day the money moved is
          what "last week" means to whoever is asking. */}
      <Field label="Settled from">
        <Input
          type="date"
          value={search.start_date ?? ""}
          onChange={(event) => onChange({ start_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      <Field label="To">
        <Input
          type="date"
          value={search.end_date ?? ""}
          onChange={(event) => onChange({ end_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      {hasFilters ? (
        <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
          <XIcon className="size-3.5" />
          Clear
        </Button>
      ) : null}
    </div>
  );
}
